import Decimal from 'decimal.js';
import { toMg, mgPerMl, mlPerInjection, formatDose, formatConcentration, toIU } from './unitNormalization';

/**
 * Input parameters for dose calculation.
 */
export interface CalculatorInput {
  /** Vial amount (in the unit specified by vialUnit) */
  vialAmount: number | string;
  /** Unit of the vial amount */
  vialUnit: 'mg' | 'mcg' | 'IU';
  /** Reconstitution volume in milliliters */
  reconVolumeMl: number | string;
  /** Desired dose (in the unit specified by desiredDoseUnit) */
  desiredDose: number | string;
  /** Unit of the desired dose */
  desiredDoseUnit: 'mg' | 'mcg' | 'IU';
  /** IU per mg conversion factor (required when using IU units) */
  iuPerMg?: number;
  /** Rounding mode for mL per injection (default: 0.01) */
  roundingMode?: 0.1 | 0.01 | 0.001;
}

/**
 * Normalized calculation results using Decimal.js for precision.
 */
export interface NormalizedResults {
  /** Vial amount normalized to mg */
  vialAmountMg: Decimal;
  /** Desired dose normalized to mg */
  desiredDoseMg: Decimal;
  /** Concentration in mg/mL */
  concentrationMgPerMl: Decimal;
  /** Milliliters per injection (precise, before rounding) */
  mlPerInjection: Decimal;
  /** Total injections possible from vial */
  totalInjections: Decimal;
}

/**
 * Formatted display strings for UI presentation.
 */
export interface DisplayResults {
  /** Volume to draw per injection (rounded) */
  volumeToDraw: string;
  /** Concentration in mg/mL */
  concentration: string;
  /** Dose per injection in original unit */
  dosePerInjection: string;
  /** Total injections possible */
  totalInjections: string;
  /** IU equivalent (if applicable) */
  iu?: string;
  /** Note about rounding applied */
  roundingNote?: string;
}

/**
 * Complete calculation result with both normalized and display values.
 */
export interface CalculatorResult {
  /** Normalized Decimal values for further calculations */
  normalized: NormalizedResults;
  /** Formatted display strings for UI */
  display: DisplayResults;
}

/**
 * Round a Decimal value to the nearest specified increment.
 *
 * @param value - The value to round
 * @param increment - The rounding increment (0.1, 0.01, or 0.001)
 * @returns Rounded Decimal value
 */
function roundToIncrement(value: Decimal, increment: 0.1 | 0.01 | 0.001): Decimal {
  const incrementDecimal = new Decimal(increment);
  return value.dividedBy(incrementDecimal).toNearest(1).times(incrementDecimal);
}

/**
 * Format mL value with appropriate precision based on rounding mode.
 *
 * @param value - The mL value to format
 * @param roundingMode - The rounding mode used
 * @returns Formatted string (e.g., "0.1 mL" or "0.01 mL")
 */
function formatMl(value: Decimal, roundingMode: 0.1 | 0.01 | 0.001): string {
  let precision: number;
  switch (roundingMode) {
    case 0.1:
      precision = 1;
      break;
    case 0.01:
      precision = 2;
      break;
    case 0.001:
      precision = 3;
      break;
  }
  return `${value.toFixed(precision)} mL`;
}

/**
 * Generate rounding note if rounding was applied.
 *
 * @param original - Original precise value
 * @param rounded - Rounded value
 * @param roundingMode - Rounding mode used
 * @returns Note string or undefined if no rounding occurred
 */
function generateRoundingNote(
  original: Decimal,
  rounded: Decimal,
  roundingMode: 0.1 | 0.01 | 0.001
): string | undefined {
  if (original.equals(rounded)) {
    return undefined;
  }
  const modeText = roundingMode === 0.1 ? '0.1 mL' : roundingMode === 0.01 ? '0.01 mL' : '0.001 mL';
  return `Volume rounded to nearest ${modeText}`;
}

/**
 * Calculate dose calculations for peptide reconstitution and injection.
 *
 * This function normalizes all inputs to mg internally, performs precise calculations
 * using Decimal.js, and returns both normalized values and formatted display strings.
 *
 * @param input - Calculation input parameters
 * @returns CalculatorResult with normalized values and display strings
 * @throws Error if inputs are invalid
 */
export function calculateDose(input: CalculatorInput): CalculatorResult {
  const {
    vialAmount,
    vialUnit,
    reconVolumeMl,
    desiredDose,
    desiredDoseUnit,
    iuPerMg,
    roundingMode = 0.01,
  } = input;

  // Check if IU conversion is needed
  const needsIuPerMg = (vialUnit === 'IU' || desiredDoseUnit === 'IU');
  if (needsIuPerMg && !iuPerMg) {
    throw new Error('IU/mg conversion factor is required for IU calculations');
  }

  // Normalize vial amount to mg
  const vialAmountMg = toMg(vialAmount, vialUnit, iuPerMg);

  // Normalize desired dose to mg
  const desiredDoseMg = toMg(desiredDose, desiredDoseUnit, iuPerMg);

  // Validate inputs
  if (vialAmountMg.isZero() || vialAmountMg.isNegative()) {
    throw new Error('Vial amount must be a positive number');
  }

  if (desiredDoseMg.isZero() || desiredDoseMg.isNegative()) {
    throw new Error('Desired dose must be a positive number');
  }

  if (desiredDoseMg.greaterThan(vialAmountMg)) {
    throw new Error('Desired dose cannot exceed vial amount');
  }

  // Calculate concentration in mg/mL
  const concentrationMgPerMl = mgPerMl(vialAmountMg.toNumber(), reconVolumeMl);

  // Calculate mL per injection (precise)
  const mlPerInjectionPrecise = mlPerInjection(desiredDoseMg.toNumber(), concentrationMgPerMl.toNumber());

  // Apply rounding to mL per injection
  const mlPerInjectionRounded = roundToIncrement(mlPerInjectionPrecise, roundingMode);

  // Calculate total injections
  const totalInjections = vialAmountMg.dividedBy(desiredDoseMg);

  // Format display strings
  const display: DisplayResults = {
    volumeToDraw: formatMl(mlPerInjectionRounded, roundingMode),
    concentration: formatConcentration(concentrationMgPerMl),
    dosePerInjection: formatDose(desiredDose, desiredDoseUnit),
    totalInjections: `${totalInjections.toFixed(1)} injections`,
    roundingNote: generateRoundingNote(mlPerInjectionPrecise, mlPerInjectionRounded, roundingMode),
  };

  // Add IU equivalent if using IU units or if iuPerMg is provided
  if (iuPerMg && (vialUnit === 'IU' || desiredDoseUnit === 'IU')) {
    const iuEquivalent = toIU(desiredDoseMg.toNumber(), 'mg', iuPerMg);
    display.iu = formatDose(iuEquivalent, 'IU');
  }

  // Build normalized results
  const normalized: NormalizedResults = {
    vialAmountMg,
    desiredDoseMg,
    concentrationMgPerMl,
    mlPerInjection: mlPerInjectionPrecise, // Keep precise value in normalized
    totalInjections,
  };

  return {
    normalized,
    display,
  };
}
